/**
 * The tenant health strip — a live cross-room rollup (counts) sourced from
 * `GET /api/health/summary`. Replaces the former PLANNED placeholder. Each stat is
 * token-coloured (OK green, Degraded amber, Error red) so an operator reads the
 * tenant's state at a glance; "Active alerts" deep-links to the Alerts screen.
 */

import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { RefreshIcon } from '@8x8/oxygen-icon';
import { IconButton } from '@8x8/oxygen-button';
import type { HealthSummaryResponse } from '@spaces/contracts';
import { getHealthSummary } from '../api';
import { navigate } from '../router';
import { Skel } from './primitives';

const Strip = styled.div`
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 18px;
    background: ${p => p.theme.ui06};
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
`;

const Label = styled.span`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: ${p => p.theme.textColor05};
`;

const Cards = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

const StatCard = styled.button<{ $accent?: string; $clickable?: boolean }>`
    flex: 1;
    min-width: 116px;
    text-align: left;
    border: 1px solid ${p => p.theme.ui01};
    border-left: 3px solid ${p => p.$accent ?? p.theme.ui32};
    border-radius: 6px;
    padding: 10px 12px;
    background: ${p => p.theme.ui31};
    cursor: ${p => (p.$clickable ? 'pointer' : 'default')};

    &:hover {
        border-color: ${p => (p.$clickable ? p.theme.action08 : p.theme.ui01)};
    }
`;

const StatLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${p => p.theme.textColor05};
`;

const StatValue = styled.div<{ $color?: string }>`
    font-size: 20px;
    line-height: 26px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${p => p.$color ?? p.theme.textColor02};
    margin-top: 2px;
`;

const Err = styled.div`
    font-size: 12.5px;
    color: ${p => p.theme.warning03};
`;

export function HealthStrip({ tenant, reloadKey = 0 }: { tenant: string; reloadKey?: number }): JSX.Element {
    const theme = useTheme();
    const [summary, setSummary] = useState<HealthSummaryResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [tick, setTick] = useState(0);
    const reqId = useRef(0);

    useEffect(() => {
        const id = ++reqId.current;

        setError(null);
        void getHealthSummary(tenant).then(result => {
            if (id !== reqId.current) {
                return;
            }
            if (result.ok) {
                setSummary(result.data);
            } else {
                setError(result.message);
            }
        });
    }, [tenant, reloadKey, tick]);

    // Light auto-refresh so the board tracks liveness without a manual reload.
    useEffect(() => {
        const t = setInterval(() => setTick(n => n + 1), 20_000);

        return () => clearInterval(t);
    }, []);

    const stats: { label: string; value: number | undefined; color?: string; to?: string }[] = [
        { label: 'Rooms', value: summary?.total },
        { label: 'OK', value: summary?.ok, color: theme.success01 },
        { label: 'Degraded', value: summary?.degraded, color: theme.warning03 },
        { label: 'Error', value: summary?.error, color: theme.error01 },
        { label: 'TV online', value: summary?.tvOnline, color: theme.success01 },
        { label: 'Active alerts', value: summary?.activeAlerts, color: theme.error01, to: '/alerts' }
    ];

    return (
        <Strip>
            <Head>
                <Label>TENANT HEALTH</Label>
                <IconButton title="Refresh health" onClick={() => setTick(n => n + 1)}>
                    <RefreshIcon size={14} />
                </IconButton>
            </Head>
            {error ? (
                <Err>Couldn&apos;t load health: {error}</Err>
            ) : (
                <Cards>
                    {stats.map(s => (
                        <StatCard
                            key={s.label}
                            as={s.to ? 'button' : 'div'}
                            $accent={s.color}
                            $clickable={Boolean(s.to)}
                            onClick={s.to ? () => navigate(s.to as string) : undefined}
                        >
                            <StatLabel>{s.label}</StatLabel>
                            {summary === null ? (
                                <div style={{ marginTop: 4 }}><Skel w="36px" h={20} /></div>
                            ) : (
                                <StatValue $color={s.value && s.value > 0 ? s.color : undefined}>{s.value ?? 0}</StatValue>
                            )}
                        </StatCard>
                    ))}
                </Cards>
            )}
        </Strip>
    );
}
